import { Injectable } from "@angular/core";
import { BaseState, StateServiceWithMeta } from "@core-services/state.service";
import { Dictionary } from "@helpers/types";
import { WatchlistAsset } from "@modules/watchlist/domain/entities/watchlist-asset";

export interface WatchlistStateModel extends BaseState {
	tradableIds: string[];
	watchlistSort: Dictionary<number>;
}

const initialState: WatchlistStateModel = {
	tradableIds: [],
	watchlistSort: {},
	isSuccess: false,
	isError: false
};

@Injectable({
	providedIn: "root"
})
export class WatchlistStateService extends StateServiceWithMeta<WatchlistStateModel> {
	constructor() {
		super(initialState);
	}

	public updateStateFromApiResponse(assets: WatchlistAsset[]): void {
		const ids = assets.map((a) => a.asset_id);
		const sorting = assets.reduce((acc, currentValue) => {
			acc[currentValue.asset_id] = currentValue.sort;
			return acc;
		}, {});

		this.setState({
			tradableIds: ids,
			watchlistSort: sorting,
			isSuccess: true,
			isError: false
		});
	}

	public addAsset(id: string): void {
		const newIds = [...this.state.tradableIds, id];

		const sortOrder = Math.max(...Object.values(this.state.watchlistSort)) + 1;
		const newSort = {
			...this.state.watchlistSort,
			[id]: sortOrder
		};

		this.setState({
			tradableIds: newIds,
			watchlistSort: newSort
		});
	}

	public addMultipleAssets(ids: string[]): void {
		const newIds = [...this.state.tradableIds, ...ids];
		const sort = { ...this.state.watchlistSort };

		// we want to assign the highest sort order to make sure assets are added at the end of the list
		const maxSortOrder = Math.max(...Object.values(this.state.watchlistSort)) + 1;

		// assign ascending sort order
		ids.forEach((id, index) => {
			sort[id] = maxSortOrder + index;
		});

		this.updateState({
			tradableIds: newIds,
			watchlistSort: sort
		});
	}

	public removeAsset(idToRemove: string): void {
		const newIds = [...this.state.tradableIds].filter((id) => id !== idToRemove);
		const newSort = { ...this.state.watchlistSort };
		delete newSort[idToRemove];

		this.setState({
			tradableIds: newIds,
			watchlistSort: newSort
		});
	}

	public moveAsset(fromIndex: number, toIndex: number): void {
		const currentIds = [...this.state.tradableIds];
		const idToMove = currentIds.splice(fromIndex, 1)[0];
		currentIds.splice(toIndex, 0, idToMove);

		const newWatchlistSort = {};
		const orderedWatchlist = Object.values(this.state.watchlistSort).sort((a, b) => a - b);

		// tradableIds are already sorted correctly, we just assign the watchlistSort values again in ascending order
		currentIds.forEach((key, index) => {
			newWatchlistSort[key] = orderedWatchlist[index];
		});

		this.setState({
			...this.state,
			tradableIds: currentIds,
			watchlistSort: newWatchlistSort
		});
	}
}
